import { memo, useState, useEffect } from "react";
import { Chip, Divider, Grid2, TextField } from "@mui/material";
import { icon } from "@/app/style/icons";
import { DocumentSortField, selectDocumentFilters, setJurisdictionFilter, setSearchTerm, setSortOrder, setSortField } from "@store/slices/documentSelection";
import { useDispatch, useSelector } from "react-redux";
import { Label } from "@/types/enums";
import debounce from "lodash/debounce";
import { DocumentSortFieldEnum, DocumentSortOrderEnum } from "@/app/graphql/codegen";
import { alpha, useTheme } from "@mui/material/styles";
import FilterToggleIcon from "@chat/components/sidebar/documents/FilterToggleIcon";

interface DocumentFilterProps {
    disabled?: boolean;
}

type FilterAction = "search" | "filter" | "sort";

const DocumentFilter = memo(({ disabled }: DocumentFilterProps) => {
    const theme = useTheme();
    const dispatch = useDispatch();

    // Aktuelle Filter/Sortierkriterien aus dem Store
    const filters = useSelector(selectDocumentFilters);

    // Aktivierte Filteraktionen, die angezeigt werden sollen
    const [activeActions, setActiveActions] = useState<FilterAction[]>([]);
    const toggleAction = (action: FilterAction) => {
        if (disabled) return;

        setActiveActions((current) => {
            const isCurrentlyActive = current.includes(action);
            
            // If we're turning OFF the search action, clear the search term
            if (action === "search" && isCurrentlyActive) {
                setSearchInput("");
                dispatch(setSearchTerm(""));
            }
            
            // If we're turning OFF the filter action, reset jurisdiction to 'all'
            if (action === "filter" && isCurrentlyActive) {
                dispatch(setJurisdictionFilter("all"));
            }
            
            return isCurrentlyActive ? current.filter((a) => a !== action) : [...current, action];
        });
    };

    // Suchbegriff mit Debounce (aktualisiert Filter sobald der Suchbegriff stabil bleibt)
    const [searchInput, setSearchInput] = useState(filters.searchTerm);
    const debouncedSearch = debounce((value: string) => {
        dispatch(setSearchTerm(value));
    }, 300);

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, []); // IMPORTANT: This effect should only run once on mount

    // Setzt neuen Suchbegriff und führt Suche aus
    const handleSearchChange = (value: string) => {
        if (disabled) return;

        setSearchInput(value);
        debouncedSearch(value);
    };

    // Setzt neues Jurisdiktions-Filter
    const handleJurisdictionChange = (value: string) => {
        if (disabled) return;
        dispatch(setJurisdictionFilter(value));
    };

    // Setzt neuen Sortierwert
    const handleSortChange = (field: DocumentSortField) => {
        if (disabled) return;

        if (field === filters.sortField) {
            // Toggle sort order if same field
            dispatch(setSortOrder(filters.sortOrder === DocumentSortOrderEnum.Asc ? DocumentSortOrderEnum.Desc : DocumentSortOrderEnum.Asc));
        } else {
            // Set new field and default to descending
            dispatch(setSortField(field));
            dispatch(setSortOrder(DocumentSortOrderEnum.Desc));
        }
    };

    return (
        <Grid2 container size={12} spacing={1.5}>
            <Grid2 container size={12} spacing={1} justifyContent={"space-between"}>
                <Grid2 container>
                    <Grid2>
                        <FilterToggleIcon onClick={() => toggleAction("search")} active={activeActions.includes("search")} icon={icon.search} disabled={disabled} />
                    </Grid2>

                    <Grid2>
                        <FilterToggleIcon onClick={() => toggleAction("filter")} active={activeActions.includes("filter")} icon={icon.filter} disabled={disabled} />
                    </Grid2>

                    <Grid2>
                        <FilterToggleIcon onClick={() => toggleAction("sort")} active={activeActions.includes("sort")} icon={icon.order} disabled={disabled} />
                    </Grid2>
                </Grid2>
            </Grid2>

            {activeActions.length > 0 && (
                <Grid2 container size={12} spacing={1}>
                    {activeActions.includes("search") && (
                        <Grid2 size={12}>
                            <TextField
                                size={"small"}
                                fullWidth
                                placeholder={Label.SEARCH}
                                value={searchInput}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                disabled={disabled}
                                sx={{
                                    opacity: disabled ? 0.7 : 1,
                                    "& .MuiInputBase-root": {
                                        "&.Mui-disabled": {
                                            bgcolor: "background.paper",
                                            opacity: 0.7,
                                        },
                                    },
                                }}
                            />
                        </Grid2>
                    )}

                    {activeActions.includes("filter") && (
                        <Grid2 container size={12} spacing={1}>
                            {[
                                { label: "Alle", value: "all" },
                                { label: "EU", value: "EU" },
                                { label: "Deutschland", value: "DE" },
                                { label: "DE Landesrecht", value: "DE_LANDESRECHT" },
                                { label: "Sonstige", value: "OTHER" },
                            ].map((j) => (
                                <Grid2 key={j.value}>
                                    <Chip
                                        size={"small"}
                                        clickable={!disabled}
                                        label={j.label}
                                        onClick={() => handleJurisdictionChange(j.value)}
                                        variant={filters.jurisdiction === j.value ? "filled" : "outlined"}
                                        disabled={disabled}
                                        sx={{
                                            opacity: disabled ? 0.7 : 1,
                                            "&.Mui-disabled": {
                                                bgcolor: filters.jurisdiction === j.value ? `${theme.palette.semantic.button.primary}20` : undefined,
                                                borderColor: filters.jurisdiction === j.value ? `${theme.palette.semantic.button.primary}40` : undefined,
                                            },
                                        }}
                                    />
                                </Grid2>
                            ))}
                        </Grid2>
                    )}

                    {activeActions.includes("sort") && (
                        <Grid2 container size={12} spacing={1}>
                            <Grid2>
                                <Chip
                                    size={"small"}
                                    clickable={!disabled}
                                    label={`Titel ${filters.sortField === DocumentSortFieldEnum.Title ? (filters.sortOrder === DocumentSortOrderEnum.Asc ? "↑" : "↓") : ""}`}
                                    onClick={() => handleSortChange(DocumentSortFieldEnum.Title)}
                                    variant={filters.sortField === DocumentSortFieldEnum.Title ? "filled" : "outlined"}
                                    disabled={disabled}
                                    sx={{
                                        opacity: disabled ? 0.7 : 1,
                                        "&.Mui-disabled": {
                                            bgcolor: filters.sortField === DocumentSortFieldEnum.Title ? `${theme.palette.semantic.button.primary}20` : undefined,
                                            borderColor: filters.sortField === DocumentSortFieldEnum.Title ? `${theme.palette.semantic.button.primary}40` : undefined,
                                        },
                                    }}
                                />
                            </Grid2>

                            <Grid2>
                                <Chip
                                    size={"small"}
                                    clickable={!disabled}
                                    label={`Datum ${filters.sortField === DocumentSortFieldEnum.Date ? (filters.sortOrder === DocumentSortOrderEnum.Asc ? "↑" : "↓") : ""}`}
                                    onClick={() => handleSortChange(DocumentSortFieldEnum.Date)}
                                    variant={filters.sortField === DocumentSortFieldEnum.Date ? "filled" : "outlined"}
                                    disabled={disabled}
                                    sx={{
                                        opacity: disabled ? 0.7 : 1,
                                        "&.Mui-disabled": {
                                            bgcolor: filters.sortField === DocumentSortFieldEnum.Date ? `${theme.palette.semantic.button.primary}20` : undefined,
                                            borderColor: filters.sortField === DocumentSortFieldEnum.Date ? `${theme.palette.semantic.button.primary}40` : undefined,
                                        },
                                    }}
                                />
                            </Grid2>

                            <Grid2>
                                <Chip
                                    size={"small"}
                                    clickable={!disabled}
                                    label={`Größe ${filters.sortField === DocumentSortFieldEnum.Size ? (filters.sortOrder === DocumentSortOrderEnum.Asc ? "↑" : "↓") : ""}`}
                                    onClick={() => handleSortChange(DocumentSortFieldEnum.Size)}
                                    variant={filters.sortField === DocumentSortFieldEnum.Size ? "filled" : "outlined"}
                                    disabled={disabled}
                                    sx={{
                                        opacity: disabled ? 0.7 : 1,
                                        "&.Mui-disabled": {
                                            bgcolor: filters.sortField === DocumentSortFieldEnum.Size ? `${theme.palette.semantic.button.primary}20` : undefined,
                                            borderColor: filters.sortField === DocumentSortFieldEnum.Size ? `${theme.palette.semantic.button.primary}40` : undefined,
                                        },
                                    }}
                                />
                            </Grid2>
                        </Grid2>
                    )}
                </Grid2>
            )}

            <Grid2 size={12}>
                <Divider sx={{ mb: 1.5 }} />
            </Grid2>
        </Grid2>
    );
});

export default DocumentFilter;
